import { AdsTargetingProvider } from '@folklore/ads';
import { useRoutes } from '@folklore/routes';
import { Suspense, lazy, useState } from 'react';
import { Route, Switch } from 'wouter';

import { PageContextProvider } from '../contexts/PageContext';
// import { useGlobalBrand } from '../contexts/LayoutContext';
import { useDefaultBrand } from '../contexts/SiteContext';
import { typesSections } from '../routes';
import StaticRouter, { type StaticRouterNavigate } from './StaticRouter';
import AccountPage from './pages/AccountPage';
import AiPage from './pages/AiPage';
import AllPage from './pages/AllPage';
import AuthorPage from './pages/AuthorPage';
import CategoryPage from './pages/CategoryPage';
import CollectionPage from './pages/CollectionPage';
import CollectionsPage from './pages/CollectionsPage';
import DocumentPage from './pages/DocumentPage';
import FormatPage from './pages/FormatPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
// import MapPage from './pages/MapPage';
import MicromagsPage from './pages/MicromagsPage';
import NotFoundPage from './pages/NotFoundPage';
import Page from './pages/Page';
import SearchPage from './pages/SearchPage';
import SectionPage from './pages/SectionPage';
import SubscriptionPage from './pages/SubscriptionPage';
import TopicPage from './pages/TopicPage';
import VideosPage from './pages/VideosPage';

const MapPage = lazy(() => import('./pages/MapPage'));
const ProPage = lazy(() => import('./pages/ProPage'));

export interface PageRoutesProps {
    location: string;
    pageId?: number | null;
    onNavigate?:
        ((pageId: number | null, ...args: Parameters<StaticRouterNavigate>) => void) | null;
    adsDisabled?: boolean;
    current?: boolean;
    next?: boolean;
    transitioning?: boolean;
    [key: string]: unknown;
}

function PageRoutes({
    location,
    pageId = null,
    onNavigate = null,
    adsDisabled: providedAdsDisabled = false,
    ...props
}: PageRoutesProps) {
    const { current = false, next = false, transitioning = false } = props;
    const routes = useRoutes();

    const defaultBrand = useDefaultBrand();
    const [adsDisabled, setAdsDisabled] = useState((!current && !next) || providedAdsDisabled);
    const newAdsDisabled = (!current && !next) || providedAdsDisabled;
    if (adsDisabled !== newAdsDisabled && !transitioning) {
        setAdsDisabled(newAdsDisabled);
    }
    const adsTargeting = {
        brand: defaultBrand !== null ? defaultBrand.handle || defaultBrand.slug : null,
        brands: [defaultBrand].map(({ slug = null, handle = null }) => handle || slug),
        disabled: adsDisabled,
    };

    const navigate: StaticRouterNavigate | null =
        onNavigate !== null ? (to, options) => onNavigate(pageId, to, options) : null;

    return (
        <AdsTargetingProvider targeting={adsTargeting}>
            <StaticRouter location={location} navigate={navigate}>
                <PageContextProvider>
                    <Switch>
                        {/* Collection */}
                        <Route<CollectionRouteParams> path={routes.collection}>
                            {({ slug }) => <CollectionPage slug={slug} {...props} />}
                        </Route>

                        {/* Author */}
                        <Route<AuthorRouteParams> path={routes.author}>
                            {({ author }) => <AuthorPage slug={author} {...props} />}
                        </Route>

                        {/* Taxonomy */}
                        <Route<TaxonomyRouteParams> path={routes.category}>
                            {({ slug, type }) => (
                                <CategoryPage slug={slug} type={type} {...props} />
                            )}
                        </Route>
                        <Route<TaxonomyRouteParams> path={routes.topic}>
                            {({ slug, type }) => <TopicPage slug={slug} type={type} {...props} />}
                        </Route>
                        <Route<TaxonomyRouteParams> path={routes.format}>
                            {({ slug, type }) => <FormatPage slug={slug} type={type} {...props} />}
                        </Route>

                        {/* Types sections */}
                        <Route<TypeSectionRouteParams> path={routes.micromags}>
                            {({ brand }) => <MicromagsPage brand={brand} {...props} />}
                        </Route>
                        <Route<TypeSectionRouteParams> path={routes.videos}>
                            {({ brand }) => <VideosPage brand={brand} type="videos" {...props} />}
                        </Route>
                        <Route<TypeSectionRouteParams> path={routes.podcasts}>
                            {({ brand }) => <VideosPage brand={brand} type="podcasts" {...props} />}
                        </Route>
                        <Route<TypeSectionRouteParams> path={routes.collections}>
                            {({ brand }) => (
                                <CollectionsPage brand={brand} type="collections" {...props} />
                            )}
                        </Route>
                        <Route<TypeSectionRouteParams> path={routes.series}>
                            {({ brand }) => (
                                <CollectionsPage brand={brand} type="series" {...props} />
                            )}
                        </Route>

                        {typesSections
                            .filter(({ itemsType = 'document' }) => itemsType === 'document')
                            .map(({ id, all = false }) => (
                                <Route<TypeSectionRouteParams>
                                    key={id}
                                    path={all ? routes[`${id}.all`] : routes[id]}
                                >
                                    {({ brand }) => <AllPage brand={brand} type={id} {...props} />}
                                </Route>
                            ))}

                        <Route<TypeSectionRouteParams> path={routes.sponso}>
                            {({ brand, type }) =>
                                type === 'series' || type === 'collections' ? (
                                    <CollectionsPage
                                        brand={brand}
                                        type={type === 'series' ? 'serie' : null}
                                        sponsorised
                                        {...props}
                                    />
                                ) : (
                                    <AllPage brand={brand} type={type} sponsorised {...props} />
                                )
                            }
                        </Route>

                        <Route path={routes.search}>
                            <SearchPage {...props} />
                        </Route>

                        {/* Map */}
                        <Route path={routes.map}>
                            <Suspense fallback={null}>
                                <MapPage {...props} />
                            </Suspense>
                        </Route>
                        <Route path={routes['map.region']}>
                            <Suspense fallback={null}>
                                <MapPage {...props} />
                            </Suspense>
                        </Route>
                        <Route path={routes['map.locationType']}>
                            <Suspense fallback={null}>
                                <MapPage {...props} />
                            </Suspense>
                        </Route>
                        <Route path={routes['map.location']}>
                            <Suspense fallback={null}>
                                <MapPage {...props} />
                            </Suspense>
                        </Route>
                        <Route path={routes['map.document']}>
                            <Suspense fallback={null}>
                                <MapPage {...props} />
                            </Suspense>
                        </Route>

                        {/* Auth */}
                        <Route path={routes.login}>
                            <LoginPage {...props} />
                        </Route>
                        <Route path={routes.register}>
                            <LoginPage {...props} />
                        </Route>

                        {/* Account */}
                        <Route path={routes.account}>
                            <AccountPage {...props} />
                        </Route>
                        <Route path={routes['account.subscriptions']}>
                            <AccountPage {...props} />
                        </Route>
                        <Route path={routes['account.timeline']}>
                            <AccountPage {...props} />
                        </Route>
                        <Route path={routes['account.profile']}>
                            <AccountPage {...props} />
                        </Route>
                        <Route path={routes['account.stats']}>
                            <AccountPage {...props} />
                        </Route>
                        <Route path={routes['account.promotions']}>
                            <AccountPage {...props} />
                        </Route>

                        {/* Subscription */}
                        <Route<SubscriptionRouteParams> path={routes.subscription}>
                            {({ brand }) => <SubscriptionPage brand={brand} {...props} />}
                        </Route>
                        <Route<SubscriptionRouteParams> path={routes.subscribe}>
                            {({ brand }) => <SubscriptionPage brand={brand} subscribe {...props} />}
                        </Route>
                        <Route<SubscriptionRouteParams> path={routes['subscription.friend']}>
                            {({ brand }) => <SubscriptionPage brand={brand} friend {...props} />}
                        </Route>

                        {/* Document */}
                        <Route<DocumentRouteParams> path={routes.document}>
                            {({ slug }) => <DocumentPage slug={slug} {...props} />}
                        </Route>
                        <Route<PreviewRouteParams> path={routes.preview}>
                            {({ token }) => <DocumentPage previewToken={token} {...props} />}
                        </Route>

                        {/* Home */}
                        <Route<SectionRouteParams> path={routes.section}>
                            {({ brand, section, type }) => (
                                <SectionPage brand={brand} slug={section} type={type} {...props} />
                            )}
                        </Route>
                        <Route<HomeRouteParams> path={routes.home}>
                            {({ brand }) => <HomePage brand={brand} {...props} />}
                        </Route>

                        {/* Pages */}
                        <Route path={routes.ai}>
                            <AiPage {...props} />
                        </Route>
                        <Route path={routes.pro}>
                            <Suspense fallback={null}>
                                <ProPage {...props} />
                            </Suspense>
                        </Route>
                        <Route<PageRouteParams> path={routes.page}>
                            {({ slug }) => <Page slug={slug} {...props} />}
                        </Route>
                        <Route>
                            <NotFoundPage {...props} />
                        </Route>
                    </Switch>
                </PageContextProvider>
            </StaticRouter>
        </AdsTargetingProvider>
    );
}

export default PageRoutes;
